import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import commonActions from '../../../Actions/commonActions';
import formActions from '../../../Actions/formActions';
import subscriptionActions from '../../../Actions/Voss/subscriptionActions';
import forms from '../../../Constants/forms';
import * as formValidationRules from '../../../Constants/formValidationRules';
import useModal from '../../../Hooks/useModal';
import { useStep } from '../../../Hooks/useStep';
import { useAppDispatch } from '../../../Store/types';
import { Button, ButtonDirection, ButtonOption } from '../../Fundamentals/Button';
import { StepperModal as UntypedStepperModal } from '../../Fundamentals/Modal/StepperModal';
import { ModalSize } from '../../Fundamentals/Modal/StandardModal';
import { StandardStepFooter, Step, StepBody, StepFooter, StepHeaderType } from '../../Fundamentals/Stepper/Step';
import ConfirmSubscriptionChangeForm from './ConfirmSubscriptionChangeForm';
import ConfirmSubscriptionChangeEmailTemplate from '../../Customer/VOSS/Subscriptions/ConfirmSubscriptionChangeEmailTemplate';
import type { ChangeEmail, ChangeEmailForm, ModifySubscriptionForm, ProductOffer, SalesPerson, Subscription } from './ConfirmSubscriptionChange.types';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const StepperModal: any = UntypedStepperModal;

interface RootState {
    CommonReducer: { dataLoading: { modifySubscription?: boolean; changeEmailTemplate?: boolean } };
    FormReducer: { forms: Record<string, ChangeEmailForm | undefined> };
}

interface ChangeFormFooterProps {
    needsEmail: boolean;
    onSaveWithoutNotifying: () => void;
    onLoadEmail: (onNext: () => void) => void;
    onCancel: () => void;
}

const ChangeFormFooter = ({ needsEmail, onSaveWithoutNotifying, onLoadEmail, onCancel }: ChangeFormFooterProps) => {
    const { t } = useTranslation();
    const { onNext } = useStep() as { onNext: () => void };

    return (
        <div className="w-100 d-flex justify-content-end gap-8">
            <Button onClick={onCancel}>{t('common.cancel')}</Button>
            {needsEmail ? (
                <>
                    <Button onClick={onSaveWithoutNotifying}>{t('confirmsubscriptionchange.saveWithoutNotifying')}</Button>
                    <Button onClick={() => onLoadEmail(onNext)} direction={ButtonDirection.RIGHT} option={ButtonOption.PRIMARY}>
                        {t('confirmsubscriptionchange.saveAndNotify')}
                    </Button>
                </>
            ) : (
                <Button onClick={onSaveWithoutNotifying} option={ButtonOption.PRIMARY}>
                    {t('common.save')}
                </Button>
            )}
        </div>
    );
};

interface Props {
    subscription: Subscription;
    form: ModifySubscriptionForm;
    offer: ProductOffer;
    reloadData?: () => void;
}

const ConfirmSubscriptionChangeModal = ({ subscription, form, offer, reloadData }: Props) => {
    const { t } = useTranslation();
    const dispatch = useAppDispatch();
    const { closeModal } = useModal();
    const isModifying = useSelector((state: RootState) => state.CommonReducer.dataLoading.modifySubscription);
    const templateLoading = useSelector((state: RootState) => state.CommonReducer.dataLoading.changeEmailTemplate);
    const emailForm = useSelector((state: RootState) => state.FormReducer.forms[forms.changeSubscriptionEmail]);

    const [orderType, setOrderType] = useState<string>(form.data.orderType || 'ordinary');

    const newTier = offer.tiers.find((tier) => tier.id === form.data.tierId);
    const isDowngrade = subscription.tier.id !== form.data.tierId && (newTier?.rank ?? Infinity) < subscription.tier.rank;
    const needsEmail = isDowngrade || subscription.plan.id !== form.data.planId;

    useEffect(() => {
        return () => {
            dispatch(formActions.cancelFormEditing(forms.changeSubscriptionEmail));
        };
    }, [dispatch]);

    useEffect(() => {
        if (orderType !== 'other' && form.data.salesPersonEmail !== null) {
            dispatch(commonActions.changeMultiFormField(form.id, 'salesPersonEmail', null));
        }
    }, [orderType, form.data.salesPersonEmail, dispatch, form.id]);

    useEffect(() => {
        dispatch(commonActions.changeMultiFormField(form.id, 'orderType', orderType));
    }, [orderType, dispatch, form.id]);

    const onSalesPersonChange = (salesPerson: SalesPerson | null) => {
        dispatch(commonActions.changeMultiFormField(form.id, 'salesPersonEmail', salesPerson?.email ?? null));
    };

    const onSave = (changeEmail: ChangeEmail | null) => {
        void dispatch(
            subscriptionActions.modifySubscription(form, changeEmail, () => {
                reloadData?.();
                closeModal();
            })
        );
    };

    const loadEmailThenContinue = (onNext: () => void) => {
        void dispatch(subscriptionActions.getChangeEmailTemplate(form.data.id, form.data.tierId, form.data.planId)).then((template: ChangeEmail | null) => {
            if (typeof template?.to !== 'string') {
                dispatch(commonActions.showError(t('confirmsubscriptionchange.emailUnavailable')));
                return;
            }
            dispatch(formActions.startFormEditing(forms.changeSubscriptionEmail, template, formValidationRules.ChangeSubscriptionEmail));
            onNext();
        });
    };

    const onSaveAndNotify = () => {
        if (emailForm === undefined) return;
        dispatch(formActions.submitForm(emailForm, () => onSave(emailForm.data)));
    };

    const onEmailChange = (field: keyof ChangeEmail, value: string) => {
        dispatch(formActions.changeMultiFormField(emailForm, field, value));
    };

    const onCloseClick = () => {
        dispatch(commonActions.changeMultiFormField(form.id, 'salesPersonEmail', null));
        closeModal();
    };

    return (
        <StepperModal
            title={t('confirmsubscriptionchange.title')}
            onClose={onCloseClick}
            modalSize={ModalSize.LARGE}
            isStepLoading={templateLoading === true || isModifying === true}
            stepHeaderType={StepHeaderType.NONE}
        >
            <Step>
                <StepBody>
                    <ConfirmSubscriptionChangeForm
                        subscription={subscription}
                        form={form}
                        offer={offer}
                        orderType={orderType}
                        onOrderTypeChange={setOrderType}
                        onSalesPersonChange={onSalesPersonChange}
                    />
                </StepBody>
                <StepFooter>
                    <ChangeFormFooter needsEmail={needsEmail} onSaveWithoutNotifying={() => onSave(null)} onLoadEmail={loadEmailThenContinue} onCancel={onCloseClick} />
                </StepFooter>
            </Step>
            {needsEmail && (
                <Step>
                    <StepBody>
                        <ConfirmSubscriptionChangeEmailTemplate form={emailForm} onChange={onEmailChange} />
                    </StepBody>
                    <StandardStepFooter onCancel={onCloseClick}>
                        <Button onClick={onSaveAndNotify} option={ButtonOption.PRIMARY}>
                            {t('confirmsubscriptionchange.saveAndNotify')}
                        </Button>
                    </StandardStepFooter>
                </Step>
            )}
        </StepperModal>
    );
};

export default ConfirmSubscriptionChangeModal;
